
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";

interface DiscoveryFilters {
  ageMin: number;
  ageMax: number;
  maxDistance: number;
  onlineOnly: boolean;
  withPhotoOnly: boolean;
  lookingFor: string[];
}

interface DiscoveryFiltersProps {
  filters: DiscoveryFilters;
  onFiltersChange: (filters: DiscoveryFilters) => void;
  onClose: () => void;
  isOpen?: boolean;
  onReset?: () => void;
}

const DiscoveryFilters = ({ filters, onFiltersChange, onClose, isOpen = true, onReset }: DiscoveryFiltersProps) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      const resetFilters = {
        ageMin: 18,
        ageMax: 99,
        maxDistance: 50,
        onlineOnly: false,
        withPhotoOnly: true,
        lookingFor: []
      };
      setLocalFilters(resetFilters);
      onFiltersChange(resetFilters);
    }
    onClose();
  };

  const content = (
    <div className="space-y-6">
      {/* Age Range */}
      <div>
        <Label className="text-sm font-medium">
          Age: {localFilters.ageMin} - {localFilters.ageMax}
        </Label>
        <div className="flex space-x-4 mt-2">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Min</Label>
            <Slider
              value={[localFilters.ageMin]}
              onValueChange={(value) => 
                setLocalFilters(prev => ({ ...prev, ageMin: value[0] }))
              }
              max={99}
              min={18}
              step={1}
              className="mt-1"
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Max</Label>
            <Slider
              value={[localFilters.ageMax]}
              onValueChange={(value) => 
                setLocalFilters(prev => ({ ...prev, ageMax: value[0] }))
              }
              max={99}
              min={18}
              step={1}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Distance */}
      <div>
        <Label className="text-sm font-medium">
          Distance: {localFilters.maxDistance}km
        </Label>
        <Slider
          value={[localFilters.maxDistance]}
          onValueChange={(value) => 
            setLocalFilters(prev => ({ ...prev, maxDistance: value[0] }))
          }
          max={100}
          min={1}
          step={1}
          className="mt-2"
        />
      </div>

      {/* Switches */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="online-only" className="text-sm font-medium">
            Show online only
          </Label>
          <Switch
            id="online-only"
            checked={localFilters.onlineOnly}
            onCheckedChange={(checked) => 
              setLocalFilters(prev => ({ ...prev, onlineOnly: checked }))
            }
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="photo-only" className="text-sm font-medium">
            With photos only
          </Label>
          <Switch
            id="photo-only"
            checked={localFilters.withPhotoOnly}
            onCheckedChange={(checked) => 
              setLocalFilters(prev => ({ ...prev, withPhotoOnly: checked }))
            }
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex space-x-2 pt-4">
        <Button onClick={handleReset} variant="outline" className="flex-1">
          Reset
        </Button>
        <Button onClick={handleApply} className="flex-1">
          Apply Filters
        </Button>
      </div>
    </div>
  );

  if (isOpen !== undefined) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Filters</DialogTitle>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg">Filters</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

export default DiscoveryFilters;
